import React, { useEffect, useMemo } from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";
import useDatePicker, { DatePickerHook } from "commons/hooks/input/useDatePicker";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { notEmptyValidator } from "commons/utils/validate";
import moment, { createLocalDateFromUnixMilli, createUnixMilliFromLocalDate } from "commons/utils/moment";

import { useSaveActionsContext } from "context/save";

import useCmNewsApi from "hooks/api/private/useCmNewsApi";
import { useCmNewsId } from "hooks/useId";

import { CmNewsBody, CmNewsDetails } from "types/api/cmNews";

import { getExtraErrorsDescription } from "utils/validators";

export type CmNewsFormType = {
    id?: string;
    enabled: boolean;
    date_from: number | null;
    date_to: number | null;
    text: {
        it: {
            title: string;
            description: string;
        };
        en: {
            title: string;
            description: string;
        };
    };
};

export type CmNewsFormHook = {
    datePickerHookFrom: DatePickerHook;
    datePickerHookTo: DatePickerHook;
    formHook: FormHookType<CmNewsFormType>;
};

function useCmNewsForm(): CmNewsFormHook {
    const newsId = useCmNewsId();

    const { createCmNews, updateCmNews, getOneCmNews } = useCmNewsApi();

    const schema = useMemo<ObjectPropertyType>(
        () => ({
            type: "object",
            properties: {
                id: { type: "simple", validators: [] },
                enabled: { type: "simple", validators: [] },
                date_from: { type: "simple", validators: [notEmptyValidator] },
                date_to: { type: "simple", validators: [notEmptyValidator] },
                text: {
                    type: "object",
                    properties: {
                        it: {
                            type: "object",
                            properties: {
                                title: { type: "simple", validators: [notEmptyValidator] },
                                description: { type: "simple", validators: [] },
                            },
                        },
                        en: {
                            type: "object",
                            properties: {
                                title: { type: "simple", validators: [] },
                                description: { type: "simple", validators: [] },
                            },
                        },
                    },
                },
            },
        }),
        [],
    );

    const initialValues: CmNewsFormType = React.useMemo(
        () => ({
            enabled: true,
            date_from: null,
            date_to: null,
            text: {
                it: { title: "", description: "" },
                en: { title: "", description: "" },
            },
        }),
        [],
    );

    const formHook = useForm<CmNewsFormType>(schema, initialValues, getExtraErrorsDescription);

    const { showAllError, setForm, isValid, form } = formHook;

    const [newsDetails, setNewsDetails] = React.useState<CmNewsDetails | undefined>(undefined);

    React.useEffect(() => {
        if (newsId) {
            const [apiCall, abortController] = getOneCmNews({ id: newsId });

            apiCall.then(setNewsDetails);

            return () => { abortController.abort(); };
        }
    }, [getOneCmNews, newsId]);

    React.useEffect(() => {
        if (newsDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.id = newsDetails.id;
                newForm.enabled = newsDetails.enabled;
                newForm.date_from = createUnixMilliFromLocalDate(newsDetails.date_from);
                newForm.date_to = createUnixMilliFromLocalDate(newsDetails.date_to);
                newForm.text.it.title = newsDetails.text.it?.title ?? "";
                newForm.text.it.description = newsDetails.text.it?.description ?? "";
                newForm.text.en.title = newsDetails.text.en?.title ?? "";
                newForm.text.en.description = newsDetails.text.en?.description ?? "";

                return newForm;
            });
        }
    }, [setForm, newsDetails]);

    const { addSaveAction } = useSaveActionsContext();

    useEffect(() => {
        addSaveAction("useCmNewsForm", () => {
            if (isValid) {
                const body: CmNewsBody = {
                    enabled: form.enabled,
                    date_from: form.date_from ? createLocalDateFromUnixMilli(form.date_from) : "",
                    date_to: form.date_to ? createLocalDateFromUnixMilli(form.date_to) : "",
                    text: {
                        it: {
                            title: form.text.it.title,
                            description: form.text.it.description,
                        },
                        en: {
                            title: form.text.en.title,
                            description: form.text.en.description,
                        },
                    },
                };

                if (newsId) {
                    const [apiCall] = updateCmNews({ id: newsId }, body);
                    return apiCall;
                } else {
                    const [apiCall] = createCmNews(body);
                    return apiCall;
                }
            } else {
                showAllError();
                return Promise.reject(new Error("INVALID_FORM"));
            }
        });
    }, [addSaveAction, showAllError, createCmNews, updateCmNews, isValid, newsId, form]);

    const datePickerHookFrom = useDatePicker({
        label: "Data da",
        value: form.date_from,
        path: "date_from",
        setValue: formHook.setValue,
        checkErrorVisibility: formHook.checkErrorVisibility,
        required: true,
        maxDate: moment(),
    });

    const datePickerHookTo = useDatePicker({
        label: "Data a",
        value: form.date_to,
        path: "date_to",
        setValue: formHook.setValue,
        checkErrorVisibility: formHook.checkErrorVisibility,
        required: true,
        minDate: form.date_from ? moment(form.date_from) : undefined,
    });

    return React.useMemo(
        (): CmNewsFormHook => ({
            datePickerHookFrom,
            datePickerHookTo,
            formHook,
        }),
        [datePickerHookFrom, datePickerHookTo, formHook],
    );
}

export default useCmNewsForm;
